import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  getSettings, saveSettings, uploadStyleImage, checkApiKey,
  getStyleImages, setDefaultStyleImage, deleteStyleImage, getStyleImageUrl
} from '../services/api';
import { useLang } from '../i18n/LanguageContext';

const DEFAULT_MODELS = ['gpt-image-2.5-sunburst', 'gpt-image-2', 'gpt-image-1.5'];
const DEFAULT_TEXT_MODELS = ['gpt-6.1-sol', 'gpt-5.2'];
const textModelLabel = id => id === 'gpt-6.1-sol' ? 'GPT-6.1 Sol' : id;
const modelLabel = id => ({ 'gpt-image-2.5-sunburst': 'GPT-Image-2.5 Sunburst', 'gpt-image-2': 'GPT-Image-2', 'gpt-image-1.5': 'GPT-Image-1.5' }[id] || id);
const qualitiesFor = model => model.startsWith('gpt-image-2.5-') ? ['auto', 'low', 'medium', 'high', 'xhigh', 'max'] : ['auto', 'low', 'medium', 'high'];
const MODEL_COPY = {
  en: { active: 'Active image model', models: 'Image model library', add: 'Add model', remove: 'Remove model', placeholder: 'Enter model ID, e.g. gpt-image-2.5-flare', draft: 'Model and output changes apply when you save.', activeHint: 'Select another model before removing the active one.', textModels: 'Text / vision model library', textAdd: 'Add text / vision model', textPlaceholder: 'Enter model ID, e.g. gpt-6.1-sol', text: 'Text / vision model', textHint: 'Used to analyze generated outfits and return text details.', promptHint: 'Your text prompt is sent directly to the active image model.', format: 'Output format', compression: 'JPEG / WebP compression quality', compressionHint: '0–100. Higher values preserve more detail and create larger files.', auto: 'Automatic', xhigh: 'Extra high', max: 'Maximum', invalid: 'Enter a valid model ID using letters, numbers, dots, colons, underscores or hyphens.', duplicate: 'This model is already in your list.', limit: 'You can save up to 50 models.', loadError: 'Could not load settings. Reload the page to try again.' },
  ar: { active: 'نموذج الصور النشط', models: 'قائمة نماذج الصور', add: 'إضافة نموذج', remove: 'حذف النموذج', placeholder: 'أدخل معرّف النموذج، مثل gpt-image-2.5-flare', draft: 'تُطبّق تغييرات النماذج والمخرجات عند الحفظ.', activeHint: 'اختر نموذجاً آخر قبل حذف النموذج النشط.', textModels: 'قائمة نماذج النصوص والرؤية', textAdd: 'إضافة نموذج نصوص ورؤية', textPlaceholder: 'أدخل معرّف النموذج، مثل gpt-6.1-sol', text: 'نموذج النصوص والرؤية', textHint: 'يُستخدم لتحليل الصور الناتجة وإرجاع تفاصيل الملابس نصياً.', promptHint: 'يُرسل وصفك النصي مباشرةً إلى نموذج الصور النشط.', format: 'صيغة الصورة الناتجة', compression: 'جودة ضغط JPEG / WebP', compressionHint: 'من 0 إلى 100. القيم الأعلى تحفظ تفاصيل أكثر وتنتج ملفات أكبر.', auto: 'تلقائية', xhigh: 'عالية جداً', max: 'قصوى', invalid: 'أدخل معرّف نموذج صالحاً باستخدام الأحرف والأرقام والنقاط والشرطات.', duplicate: 'هذا النموذج موجود في القائمة.', limit: 'يمكن حفظ ما يصل إلى 50 نموذجاً.', loadError: 'تعذّر تحميل الإعدادات. أعد تحميل الصفحة للمحاولة مجدداً.' }
};

const KEY_COPY = {
  en: { check: 'Check API key', hint: 'Checks the key currently entered, including unsaved changes.', checked: 'Last checked', statuses: { unchecked: 'Not checked yet', checking: 'Checking API response…', ok: 'API response OK — key accepted', missing: 'Enter an API key to check the connection', invalid_format: 'The API key contains invalid characters or is too long', invalid_key: 'API key rejected — check or replace the key', forbidden: 'Access denied — check the key’s permissions', rate_limited: 'Rate limit reached — try again shortly', unavailable: 'OpenAI is temporarily unavailable — try again', timeout: 'The API did not respond in time — try again', connection_error: 'Could not connect to OpenAI — try again', unexpected_response: 'Unexpected API response — try again', api_error: 'The API returned an error — try again', request_failed: 'Could not complete the check — reload or try again' } },
  ar: { check: 'فحص مفتاح API', hint: 'يفحص المفتاح المُدخل حالياً، بما في ذلك التغييرات غير المحفوظة.', checked: 'آخر فحص', statuses: { unchecked: 'لم يُفحص بعد', checking: 'جارٍ فحص استجابة API…', ok: 'استجابة API سليمة — المفتاح مقبول', missing: 'أدخل مفتاح API لفحص الاتصال', invalid_format: 'يحتوي المفتاح على أحرف غير صالحة أو يتجاوز الطول المسموح', invalid_key: 'رُفض مفتاح API — تحقق منه أو استبدله', forbidden: 'رُفض الوصول — تحقق من صلاحيات المفتاح', rate_limited: 'تم بلوغ حد الطلبات — حاول بعد قليل', unavailable: 'خدمة OpenAI غير متاحة مؤقتاً — حاول مجدداً', timeout: 'لم تستجب الخدمة في الوقت المحدد — حاول مجدداً', connection_error: 'تعذّر الاتصال بخدمة OpenAI — حاول مجدداً', unexpected_response: 'استجابة غير متوقعة — حاول مجدداً', api_error: 'أعادت الخدمة خطأ — حاول مجدداً', request_failed: 'تعذّر إكمال الفحص — أعد تحميل الصفحة أو حاول مجدداً' } }
};

export default function SettingsPage() {
  const { t, fontFamily, lang } = useLang();
  const copy = MODEL_COPY[lang] || MODEL_COPY.en;
  const [activeTab, setActiveTab] = useState('api');
  const keyCopy = KEY_COPY[lang] || KEY_COPY.en;
  const [keyStatus, setKeyStatus] = useState({ status: 'unchecked' });
  const keyCheckSequence = useRef(0);
  const keyCheckAbort = useRef(null);
  const keyCheckTimer = useRef(null);
  const [apiKey, setApiKey] = useState('');
  const [promptText, setPromptText] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [quality, setQuality] = useState('high');
  const [imageModel, setImageModel] = useState('gpt-image-1.5');
  const [imageModels, setImageModels] = useState(DEFAULT_MODELS);
  const [newModel, setNewModel] = useState('');
  const [outputFormat, setOutputFormat] = useState('png');
  const [outputCompression, setOutputCompression] = useState(90);
  const [textModel, setTextModel] = useState('gpt-5.2');
  const [textModels, setTextModels] = useState(DEFAULT_TEXT_MODELS);
  const [newTextModel, setNewTextModel] = useState('');
  const [fontSettings, setFontSettings] = useState({ fontName: 'Noto Sans', fontSize: 14 });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [styleImages, setStyleImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const checkCurrentApiKey = useCallback(async key => {
    clearTimeout(keyCheckTimer.current);
    const sequence = ++keyCheckSequence.current;
    keyCheckAbort.current?.abort();
    const controller = new AbortController();
    keyCheckAbort.current = controller;
    setKeyStatus({ status: key.trim() ? 'checking' : 'missing' });
    if (!key.trim()) return;
    try {
      const { data } = await checkApiKey(key, controller.signal);
      if (sequence === keyCheckSequence.current) setKeyStatus(data);
    } catch (error) {
      if (sequence === keyCheckSequence.current && !controller.signal.aborted) setKeyStatus({ status: 'request_failed' });
    }
  }, []);

  useEffect(() => {
    ++keyCheckSequence.current;
    keyCheckAbort.current?.abort();
    setKeyStatus({ status: apiKey.trim() ? 'unchecked' : 'missing' });
    if (settingsLoaded) keyCheckTimer.current = setTimeout(() => checkCurrentApiKey(apiKey), 800);
    return () => {
      clearTimeout(keyCheckTimer.current);
      ++keyCheckSequence.current;
      keyCheckAbort.current?.abort();
    };
  }, [apiKey, settingsLoaded, checkCurrentApiKey]);

  const loadStyleImages = useCallback(async () => {
    try {
      const { data } = await getStyleImages();
      setStyleImages(data);
    } catch (err) {
      console.error('Failed to load style images', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getSettings();
        setApiKey(data.apiKey || '');
        setPromptText(data.promptText || '');
        setSize(data.size || '1024x1024');
        setQuality(data.quality || 'high');
        setImageModel(data.imageModel || 'gpt-image-1.5');
        setImageModels(data.imageModels || DEFAULT_MODELS);
        setOutputFormat(data.outputFormat || 'png');
        setOutputCompression(data.outputCompression ?? 90);
        setTextModel(data.textModel || 'gpt-5.2');
        setTextModels(data.textModels || DEFAULT_TEXT_MODELS);
        setFontSettings({ fontName: data.fontName || 'Noto Sans', fontSize: data.fontSize || 14 });
        setSettingsLoaded(true);
      } catch (err) {
        console.error('Failed to load settings', err);
        setMessage({ text: MODEL_COPY.en.loadError, isError: true });
      }
    };
    load();
    loadStyleImages();
  }, [loadStyleImages]);

  const handleSave = async () => {
    setSaving(true); setMessage('');
    try {
      await saveSettings({ apiKey, promptText, size, quality, imageModel, imageModels, textModel, textModels, outputFormat, outputCompression, ...fontSettings });
      showMessage(t.savedMsg);
    } catch (err) {
      showMessage('Error: ' + (err.response?.data?.message || err.message), true);
    }
    setSaving(false);
  };

  const handleAddModel = () => {
    const id = newModel.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9._:-]{0,127}$/.test(id)) return showMessage(copy.invalid, true);
    if (imageModels.includes(id)) return showMessage(copy.duplicate, true);
    if (imageModels.length >= 50) return showMessage(copy.limit, true);
    setImageModels(prev => [...prev, id]);
    setNewModel('');
  };

  const handleAddTextModel = () => {
    const id = newTextModel.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9._:-]{0,127}$/.test(id)) return showMessage(copy.invalid, true);
    if (textModels.includes(id)) return showMessage(copy.duplicate, true);
    if (textModels.length >= 50) return showMessage(copy.limit, true);
    setTextModels(prev => [...prev, id]);
    setNewTextModel('');
  };

  const handleModelChange = id => {
    setImageModel(id);
    if (!qualitiesFor(id).includes(quality)) setQuality('high');
  };

  const handleStyleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        await uploadStyleImage(base64);
        showMessage(t.styleUploaded);
        loadStyleImages();
      } catch (err) {
        showMessage('Error uploading: ' + err.message, true);
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSetDefault = async (fileName) => {
    try {
      await setDefaultStyleImage(fileName);
      showMessage(t.defaultUpdated);
      loadStyleImages();
    } catch (err) {
      showMessage('Error: ' + err.message, true);
    }
  };

  const handleDelete = async (fileName, isDefault) => {
    if (!window.confirm(t.deleteConfirmStyle)) return;
    try {
      await deleteStyleImage(fileName);
      showMessage(t.styleDeleted);
      loadStyleImages();
    } catch (err) {
      showMessage('Error: ' + err.message, true);
    }
  };

  const showMessage = (msg, isError = false) => {
    setMessage({ text: msg, isError });
    setTimeout(() => setMessage(''), 4000);
  };

  const inputClasses = "w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#FA6868] focus:border-[#FA6868] transition-shadow hover:border-gray-300 shadow-sm bg-white text-gray-800";
  const labelClasses = "flex items-center gap-1.5 text-[11px] font-semibold text-[#64748b] uppercase tracking-wide";
  const iconClasses = "material-symbols-outlined text-[16px] text-[#f59e0b]";

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12 space-y-8">

      {/* Tabs */}
      <div className="flex items-center gap-8 border-b border-border">
        <button
          onClick={() => setActiveTab('api')}
          className={`pb-3 border-b-2 font-semibold text-sm px-1 transition-colors flex items-center gap-2 ${
            activeTab === 'api' ? 'border-[#FA6868] text-[#FA6868]' : 'border-transparent text-[#64748b] hover:text-[#FA6868]'
          }`}
        >
          <span className="material-symbols-outlined text-[18px]">settings</span>
          API Configuration
        </button>
        <button
          onClick={() => setActiveTab('style')}
          className={`pb-3 border-b-2 font-semibold text-sm px-1 transition-colors flex items-center gap-2 ${
            activeTab === 'style' ? 'border-[#FA6868] text-[#FA6868]' : 'border-transparent text-[#64748b] hover:text-[#FA6868]'
          }`}
        >
          <span className="material-symbols-outlined text-[18px]">image</span>
          {t.styleImagesLabel}
        </button>
      </div>

      {/* ── API Configuration Tab ── */}
      {activeTab === 'api' && (
        <div className="bg-surface rounded-xl p-8 shadow-soft border border-border mt-4">
          <h3 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            API Configuration
          </h3>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="active-image-model" className={labelClasses}>{copy.active}</label>
                <select id="active-image-model" value={imageModel} onChange={e => handleModelChange(e.target.value)} className={`custom-select ${inputClasses}`} disabled={!settingsLoaded}>
                  {imageModels.map(id => <option key={id} value={id}>{modelLabel(id)}</option>)}
                </select>
                <p className="text-xs text-[#64748b]">{copy.promptHint}</p>
              </div>
              <div className="space-y-2">
                <label htmlFor="text-vision-model" className={labelClasses}>{copy.text}</label>
                <select id="text-vision-model" value={textModel} onChange={e => setTextModel(e.target.value)} className={`custom-select ${inputClasses}`} disabled={!settingsLoaded}>
                  {textModels.map(id => <option key={id} value={id}>{textModelLabel(id)}</option>)}
                </select>
                <p className="text-xs text-[#64748b]">{copy.textHint}</p>
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-border p-4 bg-white">
              <h4 className={labelClasses}>{copy.models}</h4>
              <div className="flex flex-wrap gap-2">
                {imageModels.map(id => (
                  <div key={id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${id === imageModel ? 'border-[#FA6868] bg-red-50' : 'border-border bg-gray-50'}`}>
                    <span className="font-mono break-all">{id}</span>
                    <button type="button" aria-label={`${copy.remove} ${id}`} title={id === imageModel ? copy.activeHint : copy.remove}
                      disabled={id === imageModel || imageModels.length === 1 || !settingsLoaded}
                      onClick={() => setImageModels(prev => prev.filter(model => model !== id))}
                      className="text-[#64748b] hover:text-red-600 disabled:opacity-25 disabled:cursor-not-allowed">
                      <span className="material-symbols-outlined text-[16px]" aria-hidden="true">close</span>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input aria-label={copy.add} value={newModel} onChange={e => setNewModel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddModel(); } }} placeholder={copy.placeholder} maxLength={128} className={inputClasses} disabled={!settingsLoaded} />
                <button type="button" onClick={handleAddModel} disabled={!settingsLoaded || !newModel.trim()} className="shrink-0 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50">{copy.add}</button>
              </div>
              <p className="text-xs text-[#64748b]">{copy.draft}</p>
            </div>
            <div className="space-y-3 rounded-xl border border-border p-4 bg-white">
              <h4 className={labelClasses}>{copy.textModels}</h4>
              <div className="flex flex-wrap gap-2">
                {textModels.map(id => (
                  <div key={id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${id === textModel ? 'border-[#FA6868] bg-red-50' : 'border-border bg-gray-50'}`}>
                    <span className="font-mono break-all">{textModelLabel(id)}</span>
                    <button type="button" aria-label={`${copy.remove} ${id}`} title={id === textModel ? copy.activeHint : copy.remove}
                      disabled={id === textModel || textModels.length === 1 || !settingsLoaded}
                      onClick={() => setTextModels(prev => prev.filter(model => model !== id))}
                      className="text-[#64748b] hover:text-red-600 disabled:opacity-25 disabled:cursor-not-allowed">
                      <span className="material-symbols-outlined text-[16px]" aria-hidden="true">close</span>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input aria-label={copy.textAdd} value={newTextModel} onChange={e => setNewTextModel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTextModel(); } }} placeholder={copy.textPlaceholder} maxLength={128} className={inputClasses} disabled={!settingsLoaded} />
                <button type="button" onClick={handleAddTextModel} disabled={!settingsLoaded || !newTextModel.trim()} className="shrink-0 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50">{copy.textAdd}</button>
              </div>
              <p className="text-xs text-[#64748b]">{copy.draft} {copy.activeHint}</p>
            </div>
            <div className="space-y-2">
              <label className={labelClasses}>
                <span className={iconClasses}>key</span>
                {t.apiKeyLabel}
              </label>
              <input
                id="openai-api-key"
                aria-label={t.apiKeyLabel}
                aria-describedby="api-key-status"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={t.apiKeyPlaceholder}
                className={inputClasses}
                style={{ fontFamily }}
              />
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div id="api-key-status" role="status" aria-live="polite" aria-atomic="true" className="space-y-1">
                  <div className={`flex items-center gap-2 text-sm font-medium ${keyStatus.status === 'ok' ? 'text-emerald-700' : ['unchecked', 'checking', 'missing'].includes(keyStatus.status) ? 'text-[#64748b]' : 'text-red-600'}`}>
                    <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${keyStatus.status === 'ok' ? 'bg-emerald-500' : keyStatus.status === 'checking' ? 'bg-amber-400 animate-pulse' : ['unchecked', 'missing'].includes(keyStatus.status) ? 'bg-gray-400' : 'bg-red-500'}`} />
                    <span>{keyCopy.statuses[keyStatus.status] || keyCopy.statuses.request_failed}</span>
                  </div>
                  {keyStatus.checkedAt && <p className="text-xs text-[#64748b]">
                    {keyCopy.checked}: {new Date(keyStatus.checkedAt).toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US')}
                    {keyStatus.httpStatus != null && ` · HTTP ${keyStatus.httpStatus}`}
                    {keyStatus.responseTimeMs != null && ` · ${keyStatus.responseTimeMs} ms`}
                  </p>}
                </div>
                <button type="button" onClick={() => checkCurrentApiKey(apiKey)} disabled={!settingsLoaded || !apiKey.trim() || keyStatus.status === 'checking'} className="shrink-0 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">{keyCopy.check}</button>
              </div>
              <p className="text-xs text-[#64748b]">{keyCopy.hint}</p>
            </div>

            <div className="space-y-2">
              <label className={labelClasses}>
                <span className={iconClasses}>description</span>
                {t.promptLabel}
              </label>
              <textarea
                value={promptText}
                onChange={e => setPromptText(e.target.value)}
                placeholder={t.promptPlaceholder}
                rows={4}
                className={`${inputClasses} resize-y min-h-[100px]`}
                style={{ fontFamily }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className={labelClasses}>
                  <span className={iconClasses}>aspect_ratio</span>
                  {t.sizeLabel}
                </label>
                <select value={size} onChange={e => setSize(e.target.value)} className={`custom-select ${inputClasses} cursor-pointer`} style={{ fontFamily }}>
                  <option value="1024x1024">{t.sizeSquare}</option>
                  <option value="1536x1024">{t.sizeLandscape}</option>
                  <option value="1024x1536">{t.sizePortrait}</option>
                  <option value="auto">{t.sizeAuto}</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className={labelClasses}>
                  <span className={iconClasses}>high_quality</span>
                  {t.qualityLabel}
                </label>
                <select value={quality} onChange={e => setQuality(e.target.value)} className={`custom-select ${inputClasses} cursor-pointer`} style={{ fontFamily }}>
                  <option value="auto">{copy.auto}</option>
                  <option value="high">{t.qualityHigh}</option>
                  <option value="medium">{t.qualityMedium}</option>
                  <option value="low">{t.qualityLow}</option>
                  {qualitiesFor(imageModel).includes('xhigh') && <option value="xhigh">{copy.xhigh}</option>}
                  {qualitiesFor(imageModel).includes('max') && <option value="max">{copy.max}</option>}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="output-format" className={labelClasses}>{copy.format}</label>
                <select id="output-format" value={outputFormat} onChange={e => setOutputFormat(e.target.value)} className={`custom-select ${inputClasses}`}>
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>
            </div>
            {outputFormat !== 'png' && <div className="space-y-2 max-w-md">
              <label htmlFor="output-compression" className={labelClasses}>{copy.compression}: {outputCompression}%</label>
              <input id="output-compression" type="range" min="0" max="100" value={outputCompression} onChange={e => setOutputCompression(Number(e.target.value))} className="w-full accent-[#FA6868]" />
              <p className="text-xs text-[#64748b]">{copy.compressionHint}</p>
            </div>}
          </div>

          <div className="flex items-center justify-end mt-10 pt-6 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving || !settingsLoaded}
              className="bg-[#FA6868] hover:bg-[#e65b5b] text-white font-semibold py-2.5 px-8 rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              style={{ fontFamily }}
            >
              <span className="material-symbols-outlined text-[18px]">save</span>
              {saving ? t.saving : t.saveBtn}
            </button>
          </div>
        </div>
      )}

      {/* ── Style Images Tab ── */}
      {activeTab === 'style' && (
        <div className="bg-surface rounded-xl p-8 shadow-soft border border-border mt-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                {t.styleImagesLabel}
              </h3>
              <span className="text-xs font-semibold text-[#64748b] bg-gray-100 px-2 py-0.5 rounded-md ml-2">{t.styleImagesCount(styleImages.length)}</span>
            </div>
            <label
              htmlFor="style-upload"
              className="px-5 py-2.5 bg-[#FA6868] text-white rounded-lg text-sm font-semibold cursor-pointer hover:bg-[#e65b5b] transition-colors shadow-sm flex items-center gap-2"
              style={{ fontFamily }}
            >
              <span className="material-symbols-outlined text-[18px]">cloud_upload</span>
              {uploading ? t.uploading : t.uploadBtn}
            </label>
            <input
              type="file" accept="image/*" onChange={handleStyleUpload}
              className="hidden" id="style-upload" disabled={uploading}
            />
          </div>

          {styleImages.length === 0 ? (
            <label htmlFor="style-upload" className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl p-12 cursor-pointer text-[#64748b] text-sm hover:border-[#FA6868] hover:bg-gray-50 transition-all duration-200 bg-white">
              <span className="material-symbols-outlined text-5xl mb-3 text-gray-300">folder_open</span>
              <div className="font-medium">{t.noStyleImages}</div>
            </label>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {styleImages.map((img) => (
                <div
                  key={img.fileName}
                  className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md ${
                    img.isDefault ? 'ring-2 ring-[#FA6868] ring-offset-2 shadow-sm' : 'border border-border hover:border-gray-300'
                  }`}
                >
                  {img.isDefault && (
                    <div className="absolute top-2 left-2 bg-[#FA6868] text-white px-2.5 py-1 rounded-md text-[10px] font-bold z-10 shadow-sm uppercase tracking-wide">{t.defaultBadge}</div>
                  )}
                  <img
                    src={getStyleImageUrl(img.fileName)}
                    alt={img.fileName}
                    className="w-full h-40 object-cover block group-hover:scale-105 transition-transform duration-300"
                    onClick={() => handleSetDefault(img.fileName)}
                  />
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <span className="text-[11px] text-white font-medium truncate max-w-[75%]" title={img.fileName}>
                      {img.fileName}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(img.fileName, img.isDefault); }}
                      className="text-white/70 hover:text-[#FA6868] transition-colors p-1 rounded-md hover:bg-white/10"
                      title="Delete"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {message && ReactDOM.createPortal(
        <div className="fixed bottom-8 inset-x-0 z-[9999] flex justify-center pointer-events-none">
          <div
            className={`pointer-events-auto px-6 py-3 rounded-xl text-sm font-semibold shadow-lg border ${
              message.isError ? 'bg-red-50 text-[#FA6868] border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'
            }`}
            style={{ animation: 'fadeIn 0.3s ease' }}
          >
            {message.text}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
